import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Donut, Legend, LineChart, UsageRing, type Series } from '../components/charts';
import { PageHeader, Select } from '../components/form';
import { Alert, Badge, Card, cn } from '../components/ui';
import { api, type SystemStatus } from '../lib/api';

interface DashboardData {
  calls: { active: number; asterisk: boolean };
  system: {
    cpuPercent: number;
    memoryPercent: number;
    diskPercent: number | null;
    diskFreeBytes: number | null;
    diskTotalBytes: number | null;
    uptimeSeconds: number;
  };
  extensions: { online: number; total: number };
  trunks: { available: number; total: number };
  sdk: { connected: number };
  security: { emergencyNumbers: number; recordingExtensions: number };
}

interface TrendRow {
  day: string;
  total: number;
  answered: number;
  missed: number;
}

const HISTORY_POINTS = 30;

function gb(bytes: number | null): string {
  if (bytes === null) return '—';
  return `${(bytes / 1e9).toFixed(1)} GB`;
}

function uptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  return days > 0 ? `${days}d ${hours}h` : `${hours}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export function Dashboard() {
  const [trendDays, setTrendDays] = useState('15');
  /** Which counter is selected. Each one owns the panel below it. */
  const [metric, setMetric] = useState<'calls' | 'cpu' | 'memory' | 'disk'>('calls');

  const { data } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/api/v1/dashboard'),
    refetchInterval: 5_000,
  });

  const { data: status } = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api.get<SystemStatus>('/api/v1/system/status'),
    refetchInterval: 15_000,
  });

  const { data: trend = [] } = useQuery({
    queryKey: ['dashboard-trend', trendDays],
    queryFn: () => api.get<TrendRow[]>(`/api/v1/dashboard/trend?days=${trendDays}`),
  });

  /**
   * Active calls over the last few minutes.
   *
   * Kept in the browser rather than stored: it is a live readout, not history, and persisting a
   * sample every five seconds forever to draw two minutes of line would be a strange trade.
   */
  const [history, setHistory] = useState<Array<{ at: Date; value: number }>>([]);
  const [cpuHistory, setCpuHistory] = useState<number[]>([]);

  useEffect(() => {
    if (!data) return;
    setHistory((current) =>
      [...current, { at: new Date(), value: data.calls.active }].slice(-HISTORY_POINTS),
    );
    setCpuHistory((current) => [...current, data.system.cpuPercent].slice(-HISTORY_POINTS));
  }, [data]);

  const callSeries: Series[] = [
    {
      label: 'Active calls',
      colour: 'var(--primary)',
      points: history.length > 1 ? history.map((h) => h.value) : [0, 0],
    },
  ];

  const cpuSeries: Series[] = [
    {
      label: 'CPU utilisation',
      colour: 'var(--warning)',
      points: cpuHistory.length > 1 ? cpuHistory : [0, 0],
    },
  ];

  const timeLabels =
    history.length > 1
      ? history.map((h) =>
          h.at.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
        )
      : ['', ''];

  const trendSeries: Series[] = [
    { label: 'Total', colour: 'var(--primary)', points: trend.map((t) => t.total) },
    { label: 'Answered', colour: 'var(--success)', points: trend.map((t) => t.answered) },
    { label: 'Missed', colour: 'var(--warning)', points: trend.map((t) => t.missed) },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Dashboard" description="What this PBX is doing right now." />

      {status?.config?.drift && (
        <Alert tone="warning">
          Asterisk is running configuration v{status.config.version}, which was rolled back after a
          failed apply. The database describes something newer, and nothing is re-applied
          automatically.
        </Alert>
      )}

      {data && !data.calls.asterisk && (
        <Alert>The call engine cannot reach Asterisk. Calls are not being handled.</Alert>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-5">
          <Card className="space-y-4">
            <div className="flex flex-wrap gap-8">
              <Counter label="Active calls" value={String(data?.calls.active ?? 0)} accent />
              <Counter label="CPU" value={`${data?.system.cpuPercent ?? 0}%`} />
              <Counter label="Memory" value={`${data?.system.memoryPercent ?? 0}%`} />
              <Counter
                label="Disk"
                value={
                  data?.system.diskPercent === null ? '—' : `${data?.system.diskPercent ?? 0}%`
                }
              />
            </div>

            <LineChart
              series={callSeries}
              labels={
                history.length > 1
                  ? history.map((h) =>
                      h.at.toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      }),
                    )
                  : ['', '']
              }
              height={220}
            />

            <Legend series={callSeries} />
          </Card>

          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <Donut
                label="Registered extensions"
                value={data?.extensions.online ?? 0}
                total={data?.extensions.total ?? 0}
                tone="success"
              />
            </Card>
            <Card>
              <Donut
                label="Trunks available"
                value={data?.trunks.available ?? 0}
                total={data?.trunks.total ?? 0}
              />
            </Card>
            <Card>
              <Donut
                label="SDK clients connected"
                value={data?.sdk.connected ?? 0}
                total={data?.sdk.connected ?? 0}
                tone="warning"
              />
            </Card>
          </div>

          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-medium">Call trend</h2>
              <div className="w-36">
                <Select value={trendDays} onChange={setTrendDays}>
                  <option value="7">Last 7 days</option>
                  <option value="15">Last 15 days</option>
                  <option value="30">Last 30 days</option>
                </Select>
              </div>
            </div>

            <LineChart
              series={trendSeries}
              labels={trend.map((t) => t.day.slice(5))}
              height={200}
            />

            <Legend series={trendSeries} />
          </Card>
        </div>

        {/* Status rail: the handful of facts an admin checks without reading anything. */}
        <div className="space-y-5">
          <Card className="space-y-1">
            <h2 className="mb-3 font-medium">System</h2>
            <StatusRow
              label="Asterisk"
              value={data?.calls.asterisk ? 'Connected' : 'Unreachable'}
              tone={data?.calls.asterisk ? 'ok' : 'bad'}
            />
            <StatusRow
              label="AMI"
              value={status?.ami.connected ? 'Connected' : 'Unreachable'}
              tone={status?.ami.connected ? 'ok' : 'bad'}
            />
            <StatusRow
              label="Configuration"
              value={status?.config ? `v${status.config.version}` : '—'}
              tone={status?.config?.drift ? 'warn' : 'ok'}
            />
            <StatusRow label="Uptime" value={data ? uptime(data.system.uptimeSeconds) : '—'} />
          </Card>

          <Card className="space-y-1">
            <h2 className="mb-3 font-medium">Storage</h2>
            <StatusRow label="Disk used" value={`${data?.system.diskPercent ?? 0}%`} />
            <StatusRow label="Free" value={gb(data?.system.diskFreeBytes ?? null)} />
            <StatusRow label="Total" value={gb(data?.system.diskTotalBytes ?? null)} />
          </Card>

          <Card className="space-y-1">
            <h2 className="mb-3 font-medium">Safety</h2>
            <StatusRow
              label="Emergency numbers"
              value={String(data?.security.emergencyNumbers ?? 0)}
              tone={(data?.security.emergencyNumbers ?? 0) > 0 ? 'ok' : 'warn'}
            />
            <StatusRow
              label="Extensions recording"
              value={String(data?.security.recordingExtensions ?? 0)}
            />
            <StatusRow
              label="Last config apply"
              value={status?.config?.status ?? '—'}
              tone={status?.config?.status === 'applied' ? 'ok' : 'warn'}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

function Counter({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div>
      <p className={cn('text-sm', accent ? 'text-primary' : 'text-muted-foreground')}>{label}</p>
      <p className="mt-0.5 text-3xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function StatusRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'bad' | undefined;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      {tone ? (
        <Badge tone={tone === 'ok' ? 'success' : tone === 'warn' ? 'warning' : 'destructive'}>
          {value}
        </Badge>
      ) : (
        <span className="tabular-nums">{value}</span>
      )}
    </div>
  );
}
